import { useEffect, useState } from 'react';
import { weatherApi } from '../../lib/api';
import { WeatherIcon } from '../ui/WeatherIcon';

interface WeatherData {
  name?: string;
  temp: number;
  feels_like: number;
  humidity: number;
  description: string;
  icon: string;
  high: number;
  low: number;
  units: string;
  wind_speed?: number;
  daily?: Array<{ date: string; high: number; low: number; icon: string }>;
}

/** Side-by-side weather for Bluffton + Fort Wayne — SVG icons (Pi-safe). */
export function WeatherWidget() {
  const [locations, setLocations] = useState<WeatherData[]>([]);
  const [error, setError] = useState('');
  const [updated, setUpdated] = useState('');

  useEffect(() => {
    let alive = true;
    const load = () => {
      weatherApi
        .getMulti()
        .then((res) => {
          if (!alive) return;
          const locs = (res.locations || []) as unknown as WeatherData[];
          setLocations(locs);
          setUpdated(String(res.updated_at || ''));
          setError('');
        })
        .catch(async () => {
          try {
            const w = (await weatherApi.get()) as unknown as WeatherData;
            if (alive) {
              setLocations([{ ...w, name: w.name || 'Home' }]);
              setError('');
            }
          } catch (e) {
            if (alive) setError(e instanceof Error ? e.message : 'Unavailable');
          }
        });
    };
    load();
    const t = setInterval(load, 10 * 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (error) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Weather
        </h3>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          Weather
        </h3>
        <p className="text-sm text-slate-500">Loading Bluffton & Fort Wayne…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        Weather · NE Indiana
      </h3>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-auto sm:grid-cols-2">
        {locations.map((data) => (
          <CityCard key={data.name || 'loc'} data={data} />
        ))}
      </div>
      {updated && (
        <p className="mt-1 text-[10px] text-slate-600">
          Updated {new Date(updated).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
}

function CityCard({ data }: { data: WeatherData }) {
  const unit = data.units === 'metric' ? '°C' : '°F';
  const shortName = (data.name || 'Local').replace(', IN', '');

  return (
    <div className="rounded-xl bg-slate-800/50 p-2.5 light:bg-slate-100">
      <div className="mb-1 text-xs font-bold tracking-wide text-violet-300">
        {shortName}
      </div>
      <div className="flex items-center gap-2">
        <WeatherIcon name={data.icon} size={40} title={data.description} />
        <div>
          <div className="text-2xl font-bold tabular-nums leading-none">
            {Math.round(data.temp)}
            {unit}
          </div>
          <div className="text-xs text-slate-400">{data.description}</div>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-slate-400">
        <span>
          H {Math.round(data.high)}° · L {Math.round(data.low)}°
        </span>
        <span>Humidity {data.humidity}%</span>
        {data.wind_speed != null && (
          <span>
            Wind {Math.round(data.wind_speed)}
            {data.units === 'metric' ? ' km/h' : ' mph'}
          </span>
        )}
      </div>
      {data.daily && data.daily.length > 1 && (
        <div className="mt-2 flex gap-1 overflow-x-auto">
          {data.daily.slice(0, 4).map((d) => (
            <div
              key={d.date}
              className="flex min-w-[2.8rem] flex-col items-center rounded bg-slate-900/40 px-1 py-0.5 text-center text-[10px] light:bg-white/80"
            >
              <div className="text-slate-500">
                {d.date.slice(5).replace('-', '/')}
              </div>
              <WeatherIcon name={d.icon} size={18} />
              <div className="tabular-nums">
                {Math.round(d.high)}°/{Math.round(d.low)}°
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
